import type { TaskSummary } from '../../../types';
import type { BoardOverviewMetrics, MetricKey } from './boardOverviewMetrics.types';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysSince(date: Date, now: Date): number {
  const diffMs = startOfDay(now).getTime() - startOfDay(new Date(date)).getTime();
  return Math.max(0, Math.floor(diffMs / MS_PER_DAY));
}

export function computeBoardOverviewMetrics(tasks: TaskSummary[], now: Date): BoardOverviewMetrics {
  const today = startOfDay(now);
  const threeDaysLater = new Date(today.getTime() + 3 * MS_PER_DAY);

  const activeTasks = tasks.filter((t) => !t.isDone);

  const openSubTasksCount = activeTasks.reduce((sum, t) => {
    return sum + Math.max(0, t.totalSubTasks - t.doneSubTasks);
  }, 0);

  const overdueCount = activeTasks.filter((t) => {
    if (!t.dueDate) return false;
    return startOfDay(new Date(t.dueDate)).getTime() < today.getTime();
  }).length;

  const atRiskCount = activeTasks.filter((t) => {
    if (!t.dueDate) return false;
    const due = startOfDay(new Date(t.dueDate)).getTime();
    return due >= today.getTime() && due <= threeDaysLater.getTime();
  }).length;

  const oldTasksCount = activeTasks.filter((t) => {
    return daysSince(new Date(t.created), now) >= 50;
  }).length;

  const avgAge =
    activeTasks.length === 0
      ? 0
      : Math.round(
          activeTasks.reduce((sum, t) => sum + daysSince(new Date(t.created), now), 0) /
            activeTasks.length,
        );

  return {
    activeTasks: activeTasks.length,
    openSubTasks: openSubTasksCount,
    overdueTasks: overdueCount,
    atRiskTasks: atRiskCount,
    oldTasks: oldTasksCount,
    avgAge,
    doneTasks: tasks.length - activeTasks.length,
  };
}

export function taskMatchesMetricFilter(
  task: TaskSummary,
  key: MetricKey,
  metrics: BoardOverviewMetrics,
  now: Date,
): boolean {
  // Every metric but one describes active work, so a done task fails them all.
  // doneTasks is that one, and it is the only key allowed past here.
  if (task.isDone) {
    return key === 'doneTasks';
  }

  const today = startOfDay(now);
  const threeDaysLater = new Date(today.getTime() + 3 * MS_PER_DAY);

  switch (key) {
    case 'doneTasks':
      return false;

    case 'activeTasks':
      return true;

    case 'openSubTasks':
      return task.totalSubTasks - task.doneSubTasks > 0;

    case 'overdueTasks': {
      if (!task.dueDate) return false;
      return startOfDay(new Date(task.dueDate)).getTime() < today.getTime();
    }

    case 'atRiskTasks': {
      if (!task.dueDate) return false;
      const due = startOfDay(new Date(task.dueDate)).getTime();
      return due >= today.getTime() && due <= threeDaysLater.getTime();
    }

    case 'oldTasks':
      return daysSince(new Date(task.created), now) >= 50;

    case 'avgAge':
      return daysSince(new Date(task.created), now) > metrics.avgAge;
  }
}
